/**
 * @format
 */
import { ValidatablePlan } from 'common-typescript';
import { Education, Plan, StudyRight, StudyTermLocator } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { pickMostRelevantStudyRight } from './study-right-entity.service';

/** The states that count as active for the "study right but none active" banner. Unlike the not ended states, they leave out PASSIVE */
const ACTIVE_STUDY_RIGHT_STATES: StudyRight['state'][] = ['ACTIVE', 'ACTIVE_NONATTENDING', 'NOT_STARTED'];
const NOT_ENDED_STUDY_RIGHT_STATES: StudyRight['state'][] = ['ACTIVE', 'ACTIVE_NONATTENDING', 'PASSIVE', 'NOT_STARTED'];

export function findMatchingStudyRight(plan: Plan, studyRights: StudyRight[]): StudyRight {
    return pickMostRelevantStudyRight(
        studyRights,
        studyRight => isForPlan(studyRight, plan) && NOT_ENDED_STUDY_RIGHT_STATES.includes(studyRight.state),
    );
}

// Same match as findMatchingStudyRight but without the study right state check, so an ended (e.g. EXPIRED) study right still
// matches. Module content workflow applications must still be able to reference the study right they were created under,
// even after it has ended - see findMatchingStudyRight for the state-restricted variant used everywhere else.
export function findMatchingStudyRightIgnoringState(plan: Plan, studyRights: StudyRight[]): StudyRight {
    return pickMostRelevantStudyRight(studyRights, studyRight => isForPlan(studyRight, plan));
}

/** The study rights of the plan's education and learning opportunity that are not deleted, in any state and in the given order */
export function studyRightsForPlan(plan: Plan, studyRights: StudyRight[]): StudyRight[] {
    return (studyRights ?? []).filter(studyRight => isForPlan(studyRight, plan));
}

/** Whether the plan has study rights but none of them is active, active non-attending or not started yet */
export function hasStudyRightForPlanButNoneActive(plan: Plan, studyRights: StudyRight[]): boolean {
    const forPlan = studyRightsForPlan(plan, studyRights);
    return forPlan.length > 0 && !forPlan.some(studyRight => ACTIVE_STUDY_RIGHT_STATES.includes(studyRight.state));
}

/** Whether the study right has no registration for the study term, or its registration is missing or neglected */
export function isTermRegistrationMissingInTerm(studyRight: StudyRight, studyTerm: StudyTermLocator): boolean {
    const termRegistration = studyRight.termRegistrations?.find(
        registration =>
            registration.studyTerm.studyYearStartYear === studyTerm?.studyYearStartYear &&
            registration.studyTerm.termIndex === studyTerm?.termIndex,
    );
    return !termRegistration || ['MISSING', 'NEGLECTED'].includes(termRegistration.termRegistrationType);
}

/**
 * The first study right of the plan's education and learning opportunity in the given order, in any state. Unlike studyRightsForPlan,
 * it also finds deleted and draft study rights, as the plan page's tutoring button always has.
 */
export function firstStudyRightForPlanIgnoringDocumentState(plan: Plan, studyRights: StudyRight[]): StudyRight | undefined {
    return studyRights?.find(studyRight => isForPlanIgnoringDocumentState(studyRight, plan));
}

function isForPlan(studyRight: StudyRight, plan: Plan): boolean {
    return isForPlanIgnoringDocumentState(studyRight, plan) && studyRight.documentState === 'ACTIVE';
}

function isForPlanIgnoringDocumentState(studyRight: StudyRight, plan: Plan): boolean {
    return studyRight.educationId === plan.rootId && studyRight.learningOpportunityId === plan.learningOpportunityId;
}

/**
 * Term registration is intentionally not checked here: professional further education (e.g. erikoislääkärikoulutus)
 * study rights are degree educations but don't require term registration, so requiring it here incorrectly blocked
 * custom attainment and inclusion applications for them. The backend validates the full eligibility rule.
 */
export function canApplyForCustomAttainments(validatablePlan: ValidatablePlan, education: Education, studyRight: StudyRight): boolean {
    if (!validatablePlan || !education || !studyRight || !isDegreeEducation(education) || studyRight.state !== 'ACTIVE') {
        return false;
    }
    const educationChildModules = validatablePlan.getSelectedModulesUnderModule(education);
    return !_.isEmpty(educationChildModules) && _.every(educationChildModules, { type: 'DegreeProgramme' });
}

function isDegreeEducation(education: Education): boolean {
    return _.startsWith(education.educationType, 'urn:code:education-type:degree-education');
}
